import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { maskTelefone, unmaskTelefone } from "@/lib/utils";
import { Mail, Phone, Plus, Trash2 } from "lucide-react";
import { UseFormReturn } from "react-hook-form";
import { CriarPacienteFormData } from "../schemas/paciente.schema";

interface AbaContatosProps {
  form: UseFormReturn<CriarPacienteFormData>;
  formData: CriarPacienteFormData;
}

export const AbaContatos = ({ 
  form,
  formData
}: AbaContatosProps) => {
  // Métodos para gerenciar emails
  const adicionarEmail = () => {
    const emailsAtuais = form.getValues("emails") || [];
    form.setValue("emails", [
      ...emailsAtuais,
      {
        email: "",
        principal: emailsAtuais.length === 0,
      },
    ]);
  };

  const removerEmail = (index: number) => {
    const emailsAtuais = form.getValues("emails") || [];
    const novosEmails = emailsAtuais.filter((_, i) => i !== index);
    form.setValue("emails", novosEmails);
  };

  const atualizarEmail = (index: number, campo: string, valor: string | boolean) => {
    const emailsAtuais = form.getValues("emails") || [];
    const novosEmails = [...emailsAtuais];
    novosEmails[index] = { ...novosEmails[index], [campo]: valor };
    form.setValue("emails", novosEmails);
  };

  // Métodos para gerenciar telefones
  const adicionarTelefone = () => {
    const telefonesAtuais = form.getValues("telefones") || [];
    form.setValue("telefones", [
      ...telefonesAtuais,
      {
        telefone: "",
        tipo: "CELULAR",
        principal: telefonesAtuais.length === 0,
      },
    ]);
  };

  const removerTelefone = (index: number) => {
    const telefonesAtuais = form.getValues("telefones") || [];
    const novosTelefones = telefonesAtuais.filter((_, i) => i !== index);
    form.setValue("telefones", novosTelefones);
  };

  const atualizarTelefone = (index: number, campo: string, valor: string | boolean) => {
    const telefonesAtuais = form.getValues("telefones") || [];
    const novosTelefones = [...telefonesAtuais];
    novosTelefones[index] = { ...novosTelefones[index], [campo]: valor };
    form.setValue("telefones", novosTelefones);
  };
  return (
    <div className="space-y-6">
      {/* Seção de Emails */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Mail className="h-5 w-5" />
              Emails
            </CardTitle>
            <Button type="button" onClick={adicionarEmail} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Email
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(formData.emails || []).length === 0 ? (
            <div>
              <div className="text-center text-muted-foreground py-8">
                Nenhum email cadastrado
              </div>
              {form.formState.errors.emails && (
                <p className="text-sm text-red-500 text-center">
                  {form.formState.errors.emails.message}
                </p>
              )}
            </div>
          ) : (
            (formData.emails || []).map((email, index) => (
              <FormField
                key={index}
                control={form.control}
                name={`emails.${index}.email`}
                render={({ field }) => (
                  <div className="flex items-end gap-4 p-4 border rounded-lg">
                    <FormItem className="flex-1">
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input
                          type="email"
                          placeholder="exemplo@email.com"
                          {...field}
                          value={field.value || ""}
                          onChange={(e) => {
                            const emailValue = e.target.value.toLowerCase().trim();
                            field.onChange(emailValue);
                            atualizarEmail(index, "email", emailValue);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`principal-email-${index}`}>Principal</Label>
                      <Switch
                        id={`principal-email-${index}`}
                        checked={email.principal || false}
                        onCheckedChange={(checked) => atualizarEmail(index, "principal", checked)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removerEmail(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              />
            ))
          )}
        </CardContent>
      </Card>

      {/* Seção de Telefones */}
      <Card>
        <CardHeader>
          <div className="flex justify-between items-center">
            <CardTitle className="flex items-center gap-2">
              <Phone className="h-5 w-5" />
              Telefones
            </CardTitle>
            <Button type="button" onClick={adicionarTelefone} size="sm">
              <Plus className="h-4 w-4 mr-2" />
              Adicionar Telefone
            </Button>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {(formData.telefones || []).length === 0 ? (
            <div>
              <div className="text-center text-muted-foreground py-8">
                Nenhum telefone cadastrado
              </div>
              {form.formState.errors.telefones && (
                <p className="text-sm text-red-500 text-center">
                  {form.formState.errors.telefones.message}
                </p>
              )}
            </div>
          ) : (
            (formData.telefones || []).map((telefone, index) => (
              <FormField
                key={index}
                control={form.control}
                name={`telefones.${index}.telefone`}
                render={({ field }) => (
                  <div className="flex items-end gap-4 p-4 border rounded-lg">
                    <FormItem className="flex-1">
                      <FormLabel>Telefone</FormLabel>
                      <FormControl>
                        <Input
                          type="tel"
                          placeholder="(62) 99999-9999"
                          maxLength={15}
                          value={maskTelefone(field.value || "")}
                          onChange={(e) => {
                            const valor = unmaskTelefone(e.target.value);
                            field.onChange(valor);
                            atualizarTelefone(index, "telefone", valor);
                          }}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                    <div className="w-32">
                      <Label htmlFor={`tipo-telefone-${index}`}>Tipo</Label>
                      <Select
                        value={telefone.tipo || "CELULAR"}
                        onValueChange={(value) => atualizarTelefone(index, "tipo", value)}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Tipo" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="CELULAR">Celular</SelectItem>
                          <SelectItem value="FIXO">Fixo</SelectItem>
                          <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                          <SelectItem value="OUTRO">Outro</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="flex items-center gap-2">
                      <Label htmlFor={`principal-telefone-${index}`}>Principal</Label>
                      <Switch
                        id={`principal-telefone-${index}`}
                        checked={telefone.principal || false}
                        onCheckedChange={(checked) => atualizarTelefone(index, "principal", checked)}
                      />
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removerTelefone(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              />
            ))
          )}
        </CardContent>
      </Card>
    </div>
  );
};
